/**
 * useBlockShipment - Hook para bloquear un shipment
 * 
 * Maneja la lógica de bloqueo de un shipment con feedback visual
 * mediante toasts de éxito y error. Requiere motivo de bloqueo y comentario.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

interface BlockShipmentData {
  shipment_block_reason_id: string;
  comment: string;
}

export const useBlockShipment = () => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const { toast } = useToast();
  const [isBlocking, setIsBlocking] = useState(false);

  const blockShipment = async (
    shipmentId: number,
    data: BlockShipmentData,
    onSuccess?: () => void
  ) => {
    setIsBlocking(true);

    try {
      console.log('🔍 useBlockShipment - Blocking shipment:', { shipmentId, data });

      const httpClient = createHttpClient(apiClientWH);
      
      // 🎯 Llamada al endpoint de bloqueo con reason_id y comment
      const response = await httpClient.post(
        `/invoices/shipments/${shipmentId}/block`,
        data
      );

      console.log('🔍 useBlockShipment - Response:', response);

      // 🎯 Mostrar toast de éxito
      toast({
        title: t('toast.block_success.title', 'Shipment bloqueado'),
        description: t('toast.block_success.description', 'El shipment se ha bloqueado exitosamente'),
        variant: 'default',
      });

      // 🎯 Ejecutar callback de éxito
      onSuccess?.();
    } catch (error: any) {
      console.log('🔍 useBlockShipment - Error:', error);

      // 🎯 Extraer mensaje de error de la respuesta
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        t('toast.block_error.unexpected', 'Error inesperado al bloquear el shipment. Inténtalo de nuevo.');

      // 🎯 Mostrar toast de error
      toast({
        title: t('toast.block_error.title', 'Error al bloquear'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsBlocking(false);
    }
  };

  return {
    blockShipment,
    isBlocking,
  };
};

